import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';

// ─── Autocorrect ──────────────────────────────────────────────────────────────

const AUTOCORRECT: Record<string, string> = {
  'entree': 'entrée', 'entrees': 'entrées',
  'Entree': 'Entrée', 'Entrees': 'Entrées',
  'ENTREE': 'ENTRÉE', 'ENTREES': 'ENTRÉES',
  'cafe': 'café', 'Cafe': 'Café', 'CAFE': 'CAFÉ',
  'a la carte': 'à la carte', 'A La Carte': 'À La Carte', 'A LA CARTE': 'À LA CARTE',
  'saute': 'sauté', 'Saute': 'Sauté', 'SAUTE': 'SAUTÉ',
  'sauteed': 'sautéed', 'Sauteed': 'Sautéed', 'SAUTEED': 'SAUTÉED',
  'puree': 'purée', 'Puree': 'Purée', 'PUREE': 'PURÉE',
  'flambe': 'flambé', 'Flambe': 'Flambé', 'FLAMBE': 'FLAMBÉ',
  'creme': 'crème', 'Creme': 'Crème', 'CREME': 'CRÈME',
  'pate': 'pâté', 'Pate': 'Pâté', 'PATE': 'PÂTÉ',
};

// Build a single regex with word boundaries, longest keys first to avoid partial matches
const AUTOCORRECT_RE = new RegExp(
  Object.keys(AUTOCORRECT)
    .sort((a, b) => b.length - a.length)
    .map((k) => `\\b${k.replace(/\s+/g, '\\s+')}\\b`)
    .join('|'),
  'g',
);

function autoCorrect(text: string): string {
  return text.replace(AUTOCORRECT_RE, (match) => {
    // Normalize whitespace in multi-word matches to find the key
    const normalized = match.replace(/\s+/g, ' ');
    return AUTOCORRECT[normalized] ?? match;
  });
}

// ─── InlineEdit ───────────────────────────────────────────────────────────────

export function InlineEdit({
  value,
  onChange,
  style,
  multiline = false,
  placeholder,
  onTab,
  editingRef,
}: {
  value: string;
  onChange: (v: string) => void;
  style?: React.CSSProperties;
  multiline?: boolean;
  placeholder?: string;
  onTab?: () => void;
  editingRef?: React.MutableRefObject<{ startEditing: () => void } | null>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  useEffect(() => {
    if (editingRef) editingRef.current = { startEditing: () => setEditing(true) };
  }, [editingRef]);

  const commit = () => {
    setEditing(false);
    const corrected = autoCorrect(draft);
    if (corrected !== value) onChange(corrected);
  };

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) {
            e.preventDefault();
            commit();
            onTab();
          }
          if (!multiline && e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        multiline={multiline}
        size="small"
        variant="standard"
        placeholder={placeholder}
        fullWidth
        sx={{ '& input, & textarea': { ...style, padding: 0 } }}
      />
    );
  }

  return (
    <span
      style={{ cursor: 'text', minWidth: 40, display: 'inline-block', ...style }}
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {value || <span style={{ color: '#aaa' }}>{placeholder}</span>}
    </span>
  );
}
